import type { LocalStore } from "@hq/local-store";
import type { SyncEngine } from "./syncEngine";

type User = { id: string; email: string };
const accountErrorMessages: Record<string, string> = {
  INITIAL_CREDENTIALS_REQUIRED:
    "Use the owner email and initial password from 1Password to set up this workspace.",
  REGISTRATION_CLOSED: "This workspace already has an account. Sign in instead.",
  USER_ALREADY_EXISTS: "This workspace already has an account. Sign in instead.",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "This workspace already has an account. Sign in instead.",
  INVALID_EMAIL_OR_PASSWORD: "Email or password was not accepted",
  PASSWORD_TOO_SHORT: "The password must be at least 12 characters.",
  ACCESS_REQUIRED: "Your access to this server expired. Sign in again to continue.",
};
class AccountRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(
      (code && Object.hasOwn(accountErrorMessages, code)
        ? accountErrorMessages[code]
        : undefined) ??
        (status === 401
          ? "Email or password was not accepted"
          : "Could not complete account request"),
    );
  }
}
const isAccessRequired = (error: unknown): error is AccountRequestError =>
  error instanceof AccountRequestError && error.code === "ACCESS_REQUIRED";
export type AccountSnapshot = {
  phase: "checking" | "signed_out" | "signed_in";
  user?: User;
  error?: string;
  /** Undefined until the server has determined whether the first account exists. */
  setupRequired?: boolean;
  /**
   * A gateway in front of the server (Cloudflare Access in production) redirected an auth request
   * to its own sign-in page. Only a full-page navigation can complete that sign-in.
   */
  accessRequired?: true;
};
export interface AccountController {
  getSnapshot(): AccountSnapshot;
  subscribe(listener: () => void): () => void;
  refresh(): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}

/** Session state never owns local data. Losing a session pauses delivery only. */
export type AuthFetch = (
  url: string,
  init: {
    method: string;
    credentials: "include";
    redirect: "manual";
    headers?: Record<string, string>;
    body?: string;
  },
) => Promise<{ ok: boolean; status: number; type?: string; json(): Promise<unknown> }>;
export function createAccountController(options: {
  url: string;
  store: LocalStore;
  sync: SyncEngine;
  flush: () => Promise<void>;
  fetch: AuthFetch;
}): AccountController {
  const fetcher = options.fetch;
  const listeners = new Set<() => void>();
  let snapshot: AccountSnapshot = { phase: "checking" };
  let generation = 0;
  let setupRequired: boolean | undefined;
  const emit = (state: AccountSnapshot) => {
    snapshot = { ...(setupRequired === undefined ? {} : { setupRequired }), ...state };
    for (const listener of listeners) listener();
  };
  async function call(path: string, body?: object) {
    const response = await fetcher(`${options.url}/${path}`, {
      method: body ? "POST" : "GET",
      credentials: "include",
      // The auth API never redirects. A redirect is a gateway asking for its own sign-in page,
      // which a fetch cannot follow across origins.
      redirect: "manual",
      ...(body
        ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
        : {}),
    });
    if (response.type === "opaqueredirect")
      throw new AccountRequestError(response.status, "ACCESS_REQUIRED");
    if (!response.ok) {
      const error: unknown = await response.json().catch(() => null);
      const code =
        error && typeof error === "object" && "code" in error && typeof error.code === "string"
          ? error.code
          : undefined;
      throw new AccountRequestError(response.status, code);
    }
    return response.json() as Promise<{ user?: User; setupRequired?: boolean } | null>;
  }
  async function accept(data: { user?: User } | null, epoch: number) {
    if (epoch !== generation) return;
    if (!data?.user) {
      options.sync.stop();
      emit({ phase: "checking" });
      const setup = await call("setup-status");
      if (epoch !== generation) return;
      if (typeof setup?.setupRequired !== "boolean")
        throw new Error("Could not check workspace setup. Please try again.");
      setupRequired = setup.setupRequired;
      emit({ phase: "signed_out" });
      return;
    }
    const user = data.user;
    if (typeof user.id !== "string" || typeof user.email !== "string") {
      options.sync.stop();
      emit({ phase: "signed_out" });
      throw new Error("Invalid session response");
    }
    const binding = await options.store.kv.get<{ url: string; userId: string }>("sync:account");
    if (binding && (binding.url !== options.url || binding.userId !== user.id)) {
      options.sync.stop();
      emit({ phase: "signed_out" });
      throw new Error(
        "This workspace belongs to another sync account. Export it before using a different account.",
      );
    }
    if (epoch !== generation) return;
    await options.store.kv.set("sync:account", { url: options.url, userId: user.id });
    if (epoch !== generation) return;
    setupRequired = false;
    emit({ phase: "signed_in", user });
    options.sync.start();
  }
  async function authenticate(path: string, email: string, password: string) {
    const epoch = ++generation;
    options.sync.stop();
    emit({ phase: "checking" });
    try {
      await accept(await call(path, { email, password, name: "Owner" }), epoch);
    } catch (error) {
      if (epoch === generation) {
        if (
          error instanceof AccountRequestError &&
          [
            "REGISTRATION_CLOSED",
            "USER_ALREADY_EXISTS",
            "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL",
          ].includes(error.code ?? "")
        )
          setupRequired = false;
        emit({
          phase: "signed_out",
          error: error instanceof Error ? error.message : "Sign-in failed",
          ...(isAccessRequired(error) ? { accessRequired: true } : {}),
        });
      }
      throw error;
    }
  }
  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async refresh() {
      const epoch = ++generation;
      if (!snapshot.user) emit({ phase: "checking" });
      try {
        await accept(await call("get-session"), epoch);
      } catch (error) {
        if (epoch !== generation) return;
        if (isAccessRequired(error)) {
          options.sync.stop();
          emit({ phase: "signed_out", error: error.message, accessRequired: true });
          return;
        }
        if (
          error instanceof AccountRequestError &&
          (error.status === 401 || error.status === 403)
        ) {
          options.sync.stop();
          emit({ phase: "signed_out", error: "Your session expired. Please sign in again." });
          return;
        }
        // Preserve a session verified in this running app during temporary network failures.
        emit({
          ...snapshot,
          phase: snapshot.user ? "signed_in" : "signed_out",
          error: error instanceof Error ? error.message : "Could not check session",
        });
      }
    },
    signIn: (email, password) => authenticate("sign-in/email", email, password),
    signUp: (email, password) => authenticate("sign-up/email", email, password),
    async signOut() {
      ++generation;
      await options.flush();
      options.sync.stop();
      emit({ phase: "signed_out" });
      await call("sign-out", {});
    },
  };
}
